import { client } from '@/services';
import Vue from 'vue';
import axios from 'axios';
import config from '@/config';

const usersService = client.service('users');
const venuesService = client.service('venues');
const users = {
  namespaced: true,
  state: {
    users: [],
    user: {},
    userVenues: [],
    jwtToken: null,
    isAuthenticated: false,
    isEnabledMFA: false,
    npUserSettings: [],
    tempPassword: null
  },
  getters: {
    GET_USERS: state => state.users,
    GET_USER: state => state.user,
    GET_AUTH_TOKEN: state => state.jwtToken,
    GET_NP_USER_SETTINGS: state => state.npUserSettingsm
  },
  mutations: {
    SET_USERS(state, users) {
      state.users = users;
    },
    ADD_MORE_USERS(state, users) {
      state.users = Array.from(new Set(state.users.concat(users)));
    },
    SET_USER(state, user) {
      state.user = user;
    },
    ADD_USER(state, user) {
      state.users.push(user);
    },
    UPDATE_USER(state, user) {
      const i = state.users.findIndex(u => u._id === user._id);
      Vue.set(state.users, i, user); // reactivity hack
    },
    REMOVE_USER(state, _id) {
      const i = state.users.findIndex(u => u._id === _id);
      state.users.splice(i, 1);
    },
    SET_AUTH_STATE(state, payload) {
      state.isAuthenticated = payload;
    },
    SET_AUTH_TOKEN(state, token) {
      state.jwtToken = token;
    },
    SET_USER_VENUES(state, payload) {
      state.userVenues = payload;
    },
    SET_NP_USER_SETTINGS(state, config) {
      state.npUserSettings = config;
    },
    SET_TEMP_PASSWORD(state, password) {
      state.tempPassword = password;
    },
    CLEAR_TEMP_PASSWORD(state) {
      state.tempPassword = null;
    }
  },
  actions: {
    async GET_USER({ commit }, userId) {
      const user = await usersService.get(userId);

      commit('SET_USER', user);

      return user;
    },
    async GET_USERS({ commit }, payload) {
      const users = await usersService.find({
        query: {
          $paginate: {
            value: false
          }
        }
      });

      commit('SET_USERS', users);
      return users;
    },
    async ADD_USER({ commit }, user) {
      try {
        user.created_at = Date.now();
        user.secret = null;
        user.firstLogin = user.isEnabledMFA;

        delete user.confirmPassword;

        const checkIfEmailExists = await usersService.find({ query: { email: user.email } });

        if (checkIfEmailExists.data.length > 0) {
          throw { message: 'Email address has already been taken!' };
        } else {
          const response = await usersService.create(user);
          commit('ADD_USER', response);
          return response;
        }
      } catch (error) {
        throw error;
      }
    },
    async UPDATE_USER({ commit, state }, payload) {
      payload.user.updated_at = Date.now();
      // if (payload.user.hasOwnProperty('isEnabledMFA')) {
      //   payload.user.firstLogin = payload.user.isEnabledMFA;
      // }

      const response = await usersService.patch(payload._id, payload.user);

      commit('UPDATE_USER', response);
      if (state && state.user && state.user._id == payload._id) {
        commit('SET_USER', response);
      }
      return response;
    },
    async UPDATE_MFA({ commit, dispatch }, payload) {
      try {
        // Just verify PIN, don't update user here
        // The update will be done in MFASetup.vue after verification
        const verifyResponse = await dispatch('verifyPIN', {
          pin: payload.pin,
          secret: payload.secret
        });

        if (!verifyResponse.success) {
          throw new Error('OTP not successfully verified. Please enter another OTP.');
        }

        return verifyResponse;
      } catch (error) {
        throw error;
      }
    },
    async UPDATE_PASSWORD({ commit, state }, user) {
      const payload = { password: user.password };
      if (user.currentPassword) {
        payload.currentPassword = user.currentPassword;
      }
      const response = await usersService.patch(user._id, payload);

      commit('UPDATE_USER', response);

      if (state.user && state.user._id === user._id) {
        commit('SET_USER', response);
      }

      return response;
    },
    async DELETE_USER({ commit }, _id) {
      const response = await usersService.remove(_id);

      commit('REMOVE_USER', _id);
      return response;
    },
    async GET_USER_VENUES({ commit }, ids) {
      try {
        const venues = await venuesService.find({
          query: {
            $paginate: {
              value: false
            },
            _id: {
              $in: ids
            }
          }
        });
        commit('SET_USER_VENUES', venues);
        return venues;
      } catch (error) {
        console.log(error);
        if (error && error.name === 'NotAuthenticated') {
          window.location.reload();
        }
      }
    },

    async LOGIN({ commit, dispatch }, login) {
      try {
        var user = null;
        user = await dispatch('LOGIN_API', login);

        if (user == null) {
          throw new Error('Login Failed...');
        }
        commit('SET_USER', user);

        if (user.isEnabledMFA) {
          if (!user.firstLogin) {
            localStorage.removeItem('feathers-jwt');
            document.cookie = 'feathers-jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
          }
        } else {
          await dispatch('LOGIN_FINAL', user);
        }

        return user;
      } catch (error) {
        throw error;
      }
    },
    async LOGIN_MFA({ commit, dispatch }, login_mfa) {
      try {
        const secret = login_mfa.user.secret;

        if (!secret) {
          throw new Error('MFA secret not found. Please contact administrator.');
        }

        // Verify PIN with the secret
        const verifyResponse = await dispatch('verifyPIN', {
          pin: login_mfa.pin,
          secret: secret
        });

        if (verifyResponse.success) {
          await dispatch('LOGIN_FINAL', login_mfa.user);
        } else {
          throw new Error('OTP not successfully verified. Please enter another OTP.');
        }

        return verifyResponse.success;
      } catch (error) {
        console.error('LOGIN_MFA error:', error);
        throw error;
      }
    },
    async LOGIN_FINAL({ commit, dispatch, getters }, user) {
      try {
        const token = getters.GET_AUTH_TOKEN;

        // add jwt to local storage and cookie
        localStorage.setItem('feathers-jwt', token);

        // add jwt to cookie
        var expirationDate = new Date();
        expirationDate.setTime(expirationDate.getTime() + 60 * 60 * 1000);
        document.cookie =
          'feathers-jwt=' + token + '; expires=' + expirationDate.toUTCString() + '; path=/;';

        // This is needed because GET_USER_VENUES uses socket-based venuesService
        // Without this, socket calls will fail with "No auth token"
        try {
          await client.authenticate();
        } catch (authError) {
          console.warn('FeathersJS socket auth during login:', authError);
          // Continue anyway - axios calls will still work
        }

        // Complete login process
        await dispatch('GET_USER_VENUES', user.venues || []);
        commit('SET_USER', user);
        commit('SET_AUTH_STATE', true);

        // Clear token from state (security)
        commit('SET_AUTH_TOKEN', null);
      } catch (error) {
        throw error;
      }
    },
    async UPDATE_FIRST_LOGIN({ commit }, userId) {
      try {
        const usersService = this.$FeathersVuex.api.service('users');
        const updatedUser = await usersService.patch(userId, {
          firstLogin: false
        });

        commit('SET_USER', updatedUser);
        return updatedUser;
      } catch (error) {
        console.error('Failed to update firstLogin:', error);
        throw error;
      }
    },
    // async LOGIN_GET_JWT({ commit }, login) {
    // 	try {
    // 		//generate token
    // 		const response = await client.authenticate({
    // 			strategy: 'local',
    // 			email: login.email,
    // 			password: login.password
    // 		});
    // 		const userId = (await client.passport.verifyJWT(response.accessToken)).userId;
    // 		const user = await usersService.get(userId);

    // 		//store on state so we can retrieve later
    // 		commit('SET_AUTH_TOKEN', response.accessToken);

    // 		return user;
    // 	} catch (error) {
    // 		console.error(error);

    // 		throw error;
    // 	}
    // },

    async LOGIN_API({ commit, dispatch }, login) {
      try {
        const response = await axios.post(`${config.server_url}/authentication`, {
          strategy: 'local',
          email: login.email,
          password: login.password
        });

        const token = response.data.accessToken;

        // Set localStorage
        localStorage.setItem('feathers-jwt', token);

        // Set cookie
        const isSecure = window.location.protocol === 'https:';
        const maxAge = 2 * 60 * 60;
        const secureSuffix = isSecure ? '; Secure' : '';
        document.cookie = `feathers-jwt=${token}; path=/; max-age=${maxAge}; SameSite=Lax${secureSuffix}`;

        // Verify JWT to get user
        const user = await dispatch('verifyJWT', token);

        // Store token in state
        commit('SET_AUTH_TOKEN', token);

        return user;
      } catch (error) {
        throw error;
      }
    },
    async LOGOUT({ commit }) {
      await axios.get(`${config.server_url}/logout`);
      await client.logout();

      localStorage.removeItem('feathers-jwt');
      document.cookie = 'feathers-jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';

      commit('SET_AUTH_STATE', false);
    },

    async request_QRCode({ commit }, userId) {
      const response = await axios.post(`${config.server_url}/api/request-qrcode`, {
        userId: userId
      });

      return response.data;
    },
    async verifyJWT({ commit }, token = null) {
      const headers = {};

      // If token is provided, use it; otherwise axios interceptor will use localStorage token
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await axios.get(`${config.server_url}/verify-jwt?_=${Date.now()}`, {
        headers,
        withCredentials: true
      });

      // return a user record
      return response.data;
    },
    async verifyPIN({ commit }, login) {
      const response = await axios.post(`${config.server_url}/api/verify-pin`, {
        pin: login.pin,
        secret: login.secret
      });
      return response.data;
    },
    async verifyPassword({ commit }, login) {
      const response = await client.authenticate({
        strategy: 'local',
        email: login.email,
        password: login.password
      });

      return response;
    },
    async getNPUserSettings({ commit }) {
      const settings = await axios.get(config.server_url + '/api/np-permissions');
      const data = JSON.parse(settings.data.config);
      commit('SET_NP_USER_SETTINGS', data);

      return data;
    }
  }
};

export default users;



// WEBPACK FOOTER //
// ./src/store/modules/users.js